/**
 * Workflows phase 4 (DEC-090): goals, the inbound webhook wait, reusable workflows, translations, path analysis and the
 * canvas. Mirrors backend `app/core/constants/workflow_p4.py`; `docs/scope/WORKFLOWS_P3_P4_CONTRACT.md` is the contract.
 */
/** The event that counts as success for a workflow (`settings.goal.event`). */
export const WORKFLOW_GOAL_EVENTS = ["booking_confirmed", "lead_created", "conversation_resolved", "conversation_rated"] as const;
export type WorkflowGoalEvent = (typeof WORKFLOW_GOAL_EVENTS)[number];
/** `settings.goal.within_hours`: 1 hour to 30 days; a new goal starts at 7 days. */
export const WORKFLOW_GOAL_HOURS = { min: 1, max: 30 * 24, default: 7 * 24 } as const;

/** "Wait for webhook" (`timeout_hours`, `example_body`) and the shape of what the client's system may send. */
export const WORKFLOW_HOOK = { timeoutHoursMin: 1, timeoutHoursMax: 168, timeoutHoursDefault: 24, exampleKeysMax: 50, valueMax: 500 } as const;
export const WORKFLOW_HOOK_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;
/** The body fields of a call become variables under this prefix (`{{webhook.order_id}}`). */
export const WORKFLOW_HOOK_VARIABLE_PREFIX = "webhook.";

/** A reusable workflow calling another one, at most this deep. */
export const REUSABLE_DEPTH_MAX = 5;

/** Languages a workflow's visitor-facing texts can be translated to; English is the primary language of every definition. */
export const WORKFLOW_PRIMARY_LANGUAGE = "en";
export const WORKFLOW_TRANSLATION_LANGUAGES = ["fil"] as const;
export type WorkflowTranslationLanguage = (typeof WORKFLOW_TRANSLATION_LANGUAGES)[number];

/** Path analysis: the periods offered, in days (the server accepts 1 to 365; default 30). */
export const WORKFLOW_ANALYSIS_DAYS = [7, 30, 90] as const;
export const WORKFLOW_ANALYSIS_DAYS_DEFAULT = 30;

/** `409` reason of a rollback refused because the draft has changes. */
export const WORKFLOW_ROLLBACK_BLOCKED_REASON = "unpublished_changes";

/** A run in one of these statuses can still be cancelled. */
export const WORKFLOW_ACTIVE_RUN_STATUSES = ["running", "waiting", "delegated"] as const;

/** The canvas view (px): the size of a tile and its rows, the gaps between tiles, and the zoom range. */
export const WORKFLOW_CANVAS = {
  tileWidth: 248,
  columnGap: 88,
  rowGap: 24,
  padding: 32,
  headerHeight: 40,
  tilePadding: 8,
  stepHeight: 36,
  stepGap: 4,
  /** The "Add step" row at the foot of an editable tile. */
  footerHeight: 36,
  triggerHeight: 88,
  zoomMin: 0.5,
  zoomMax: 1.5,
  zoomStep: 0.1,
  zoomDefault: 1,
  /** Arrow keys pan this far (px). */
  panStep: 64,
  /** One exit of a step (P5-1): its row under the step, how far it is indented, and the handle on the tile's right border (WCAG 2.5.8). */
  exitHeight: 28,
  exitIndent: 16,
  handleSize: 24,
  /** Dragging a connector within this many px of the canvas edge pans it, at most `autoPanMaxStep` px per frame. */
  autoPanEdge: 48,
  autoPanMaxStep: 18,
  /** With reduced motion the canvas pans in discrete steps instead. */
  autoPanReducedStep: 64,
  autoPanReducedMs: 150,
  /** A connector dropped on empty canvas puts the new path's header this far to the right of the drop point. */
  dropNewPathOffset: 24,
  /** The pointer must travel this far (px) before a press on a handle counts as a drag rather than a click. */
  dragThreshold: 4,
  /** How long a tile reached with "Go to" keeps its highlight ring. */
  highlightMs: 1200,
} as const;
